import { createReference } from '@medplum/core';
import { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';
import { TreatmentPaymentOrder } from '@/components/Timeline/types';
import { buildPatientSearchQuery } from '@/services/patient-search';
import { fetchBotRead } from '@/services/botCalls';
import { ConnectCandidate, rankCandidates } from './ledgerConnect';

/**
 * Patients matching `term`, each with their open order, ranked so an open
 * balance of `amount` comes first. Shared by Connect (BAR-3032) and the manual
 * entry drawer (BAR-3031).
 */
export function usePatientCandidates(
  term: string,
  amount: number
): { candidates: ConnectCandidate[]; loading: boolean; failed: boolean; retry: () => void } {
  const medplum = useMedplum();
  // Bumped by Retry: a failed read must be retryable without changing the search (BAR-3125).
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<{ term: string; candidates: ConnectCandidate[]; failed?: boolean } | undefined>();
  const retry = useCallback(() => {
    // Back to loading, so the failure clears while the search runs again.
    setState(undefined);
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const search = buildPatientSearchQuery(term);
    if (!search) {
      setState({ term, candidates: [] });
      return undefined;
    }

    async function load(): Promise<ConnectCandidate[]> {
      const response = (await medplum.graphql(search!.query, null, search!.variables)) as {
        data?: { PatientList?: Patient[] };
      };
      const patients = response.data?.PatientList ?? [];
      if (patients.length === 0) {
        return [];
      }
      const { orders } = await fetchBotRead<{
        orders: { medplumPatientId: string; order: TreatmentPaymentOrder | null }[];
      }>(medplum, 'create-treatment-payment', {
        operation: 'orderStatusByPatients',
        patients: patients.map((patient) => createReference(patient)),
      });
      const orderByPatient = new Map(orders.map(({ medplumPatientId, order }) => [medplumPatientId, order]));
      return rankCandidates(
        patients.map((patient) => ({ patient, order: orderByPatient.get(patient.id as string) ?? undefined })),
        amount
      );
    }

    load()
      .then((candidates) => !cancelled && setState({ term, candidates }))
      // A failed read is not "no patients" (BAR-3125).
      .catch(() => !cancelled && setState({ term, candidates: [], failed: true }));
    return () => {
      cancelled = true;
    };
  }, [medplum, term, amount, attempt]);

  const settled = state?.term === term;
  // The last results stay up while a new term loads, as before; its failure does not.
  return { candidates: state?.candidates ?? [], loading: !settled, failed: settled && Boolean(state.failed), retry };
}
